import React from "react";
import { FlexboxProps, LayoutProps } from "styled-system";
import ReactSelect, {
  type DropdownIndicatorProps,
  components,
  ActionMeta,
} from "react-select";
import Box from "../Box";
import { Choice } from "../../types/commercial";
import { useTranslation } from "react-i18next";

const IconDropDown = () => {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M11.4697 8.46967C11.7626 8.17678 12.2374 8.17678 12.5303 8.46967L18.5303 14.4697C18.8232 14.7626 18.8232 15.2374 18.5303 15.5303C18.2374 15.8232 17.7626 15.8232 17.4697 15.5303L12 10.0607L6.53033 15.5303C6.23744 15.8232 5.76256 15.8232 5.46967 15.5303C5.17678 15.2374 5.17678 14.7626 5.46967 14.4697L11.4697 8.46967Z"
        fill="#0970E6"
      />
    </svg>
  );
};
const IconDropUp = () => {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M5.46967 8.46967C5.76256 8.17678 6.23744 8.17678 6.53033 8.46967L12 13.9393L17.4697 8.46967C17.7626 8.17678 18.2374 8.17678 18.5303 8.46967C18.8232 8.76256 18.8232 9.23744 18.5303 9.53033L12.5303 15.5303C12.2374 15.8232 11.7626 15.8232 11.4697 15.5303L5.46967 9.53033C5.17678 9.23744 5.17678 8.76256 5.46967 8.46967Z"
        fill="#0970E6"
      />
    </svg>
  );
};
const DropdownIndicator: React.FC<DropdownIndicatorProps> = (props) => {
  return (
    <components.DropdownIndicator {...props}>
      {props.isFocused ? <IconDropDown /> : <IconDropUp />}
    </components.DropdownIndicator>
  );
};

type SelectOptionProps = {
  value: string;
  disabled?: boolean;
  handleChange: (choice: Choice | null) => void;
  list: Choice[];
} & LayoutProps &
  FlexboxProps;

const customStyles = {
  control: (styles: any) => ({
    ...styles,
    backgroundColor: "white",
    height: "auto",
    maxHeight: 600,
    maxWidth: "1OO%",
    display: "flex",
    textAlign: "right",
    fontSize: "16px",
    borderRadius: "4px",
    fontFamily: "system-ui",
    "&:hover": {
      borderBottomLeftRadius: "1px",
      borderBottomRightRadius: "1px",
    },
  }),
  menu: (base: any) => ({
    ...base,
    margin: "0px",
    width: "100%",
    borderRadius: "0px",
    fontFamily: "system-ui",
    padding: "0px",
    borderBottomLeftRadius: "4px",
    borderBottomRightRadius: "4px",
    border: "2px solid #0970E6",
  }),
  option: (styles: any, state: any) => {
    return {
      ...styles,
      color: "#121212",
      backgroundColor: state.isSelected ? "#E5E5E5" : "white",
      cursor: "pointer",
      fontFamily: "system-ui",
      textAlign: "left",
      fontSize: "14px",
      marginBottom: "12px",
      lineHeight: "20px",
      whiteSpace: "pre",
      ":hover": {
        backgroundColor: "#E5E5E5",
      },
    };
  },

  indicatorSeparator: () => ({ display: "none" }),
  singleValue: (styles: any) => {
    return {
      ...styles,
      color: "#121212",
      textAlign: "start",
      fontFamily: "system-ui",
    };
  },
  multiValue: (styles: any) => {
    return {
      ...styles,
      backgroundColor: "#7cb1ef80",
      padding: "4px 8px",
      borderRadius: "12px",
      fontFamily: "system-ui",
    };
  },
  multiValueLabel: (styles: any) => ({
    ...styles,
    color: "#002f65",
    fontFamily: "system-ui",
  }),
  multiValueRemove: (styles: any) => ({
    ...styles,
    color: "#002f65",
    fontFamily: "system-ui",
    ":hover": {
      backgroundColor: "#9c0000b0",
      color: "white",
    },
  }),
  input: (styles: any) => ({
    ...styles,
    color: "#002f65",
    fontFamily: "system-ui",
  }),
  placeholder: (styles: any) => ({
    ...styles,
    color: "#dbdbdb",
    textAlign: "left",
    fontFamily: "system-ui",
  }),
};
const SelectOption = ({
  list,
  value,
  disabled,
  handleChange,
  width,
  ...rest
}: SelectOptionProps) => {
  const find = list?.find((val) => val.value === value) as Choice;
  const OnChange = (newValue: unknown, actionMeta: ActionMeta<unknown>) => {
    const choice = newValue as Choice;
    handleChange(choice);
  };

  const {t} = useTranslation();

  return (
    <Box
      data-testid={"select-dropdown"}
      width={width || ["100%", "50%"]}
      display={"flex"}
      flexDirection={"column"}
      alignSelf={"center"}
    >
      <ReactSelect
        name="select-dropdown"
        menuPortalTarget={document.body}
        isSearchable={false}
        options={list}
        components={{ DropdownIndicator }}
        styles={customStyles}
        noOptionsMessage={() => t("common.pas_de_temporalite")}
        value={find}
        onChange={OnChange}
        isOptionSelected={(val) => val === find}
        maxMenuHeight={200}
      />
    </Box>
  );
};

export default SelectOption;
